import {gql} from '@apollo/client';
import {useCallback} from 'react';

import {PolymerSubtype, SEQUENCE_SUBTYPE_TO_POLYMER_SUBTYPE} from 'constants/MolbioConstants';
import {SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP} from 'constants/SequenceConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  BulkSequenceTypeQuery,
  BulkSequenceTypeQueryVariables,
} from 'molbio/bulk-assembly/add-polymers/__generated/BulkSequenceTypeQuery';
import {getSequenceSubtype} from 'molbio/SeqUtil';
import WorkspaceStore, {WorkspaceItemVariant} from 'sidebar/WorkspaceStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import APIUtil from 'util/APIUtil';

export const BULK_SEQUENCE_TYPE_QUERY = gql`
  query BulkSequenceTypeQuery($sequenceIds: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    sequences(ids: $sequenceIds) {
      ... on SequenceNode {
        id
        sequenceType
        nucleotideType
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface PolymerData {
  apiIdentifier: string;
  name: string;
  polymerSubtype: PolymerSubtype;
}

interface BasicPolymerData {
  apiIdentifier: string;
  name: string;
  // Will not know the actual sequence subtype until we query GQL.
  polymerSubtype?: typeof PolymerSubtype.aa_sequence;
}

function getPolymerDataFromWorkspaceItems(
  workspaceItems: Array<WorkspaceItemVariant>
): [Array<BasicPolymerData>, Array<PolymerData>] {
  const sequenceItems = workspaceItems
    .filter(({fileId}) => APIUtil.isNucleotideSequence(fileId))
    .map(({fileId, name}) => ({
      apiIdentifier: fileId,
      name,
    }));
  const proteinItems = workspaceItems
    .filter(({fileId}) => APIUtil.isProtein(fileId))
    .map(({fileId, name}) => ({
      apiIdentifier: fileId,
      name,
      polymerSubtype: PolymerSubtype.aa_sequence,
    }));
  return [sequenceItems, proteinItems];
}

/**
 * Hook to fetch and track polymer data (name, API Identifier, and model type) of all open polymers matching criteria.
 *
 * Users can pass a list of valid polymer subtypes to filter down to. It's trivial to find all open
 * nucleotide sequences in the Workspace, but information about the sequence subtypes require a network call
 * because some workspace items may not be loaded in which case we have limited metadata about them.
 */
export default function useFilteredCurrentlyOpenPolymerData(
  validPolymerSubtypes: Array<PolymerSubtype>
): Array<PolymerData> {
  // Listen to open sequence data from the WorkspaceStore.
  const [sequenceData, proteinData] = useStoreSelector(
    WorkspaceStore,
    useCallback(
      (workspaceStore: typeof WorkspaceStore) =>
        getPolymerDataFromWorkspaceItems(workspaceStore.workspaceItems),
      []
    )
  );
  const validatedProteins = validPolymerSubtypes.includes(PolymerSubtype.aa_sequence) ? proteinData : [];
  const sequenceTypeQueryResult = useSkippableBenchlingQuery<
    BulkSequenceTypeQuery,
    BulkSequenceTypeQueryVariables
  >(BULK_SEQUENCE_TYPE_QUERY, {
    variables: {sequenceIds: sequenceData.map(({apiIdentifier}) => apiIdentifier)},
    skip: sequenceData.length === 0,
  });

  // If the query is skipped (no sequences open), return just the proteins. Otherwise, wait for the query to
  // complete.
  if (sequenceTypeQueryResult.type === QueryResultType.SKIPPED) {
    return validatedProteins;
  } else if (sequenceTypeQueryResult.type !== QueryResultType.SUCCESS) {
    return [];
  }

  const polymerSubtypesBySequenceId = accessibleObjs(sequenceTypeQueryResult.data.sequences).reduce(
    (acc: {[sequenceId: string]: PolymerSubtype | undefined}, sequence) => {
      acc[sequence.id] =
        SEQUENCE_SUBTYPE_TO_POLYMER_SUBTYPE[
          getSequenceSubtype(
            SEQUENCE_TYPE_GQL_TO_NATIVE_ENUM_MAP[sequence.sequenceType],
            sequence.nucleotideType
          )
        ];
      return acc;
    },
    {}
  );

  const validatedSequences = sequenceData
    .map((data) => ({
      ...data,
      polymerSubtype: polymerSubtypesBySequenceId[data.apiIdentifier],
    }))
    .filter(
      ({polymerSubtype}) => polymerSubtype != null && validPolymerSubtypes.includes(polymerSubtype)
    ) as Array<PolymerData>;
  return validatedProteins.concat(validatedSequences);
}
